/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { FilterableShowMoreGridSection } from '@coursera-web/cds-labs';
import type { BaseItem, GridConfig } from '@coursera-web/cds-labs';

import type { TrackingData } from 'js/lib/retracked';

import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import EmptyFilteredCollectionMessage from 'bundles/unified-common/components/EmptyFilteredCollectionMessage';

type Filter = {
  /**
   * Id of the filter.
   */
  id: string;

  /**
   * Label of the filter to be displayed.
   */
  label: string;
};

type Collection<I> = {
  /**
   * Id of the collection. Should correspond to a filter id.
   */
  id: string;
  /**
   * Items within this collection
   */
  items: I[];
};

type Props<I, T> = {
  title: React.ReactNode;
  Component: React.ElementType;
  componentProps: (item: I, itemIndex: number) => T;
  /**
   * List of collections to render. The currently selected filter determines which collection is shown.
   */
  collections: Array<Collection<I>>;
  secondaryCta?: React.JSX.Element;
  itemsGridConfig?: GridConfig;
  numDisplayedRows?: number;
  /**
   * Max number of items to show for every progressive expansion
   * @default 8
   */
  showMoreAmount?: number;
  footer?: React.JSX.Element;
  trackingData?: TrackingData;
  customComponentAtIndex?: { index: number; component: React.JSX.Element };
  /**
   * Array of filter items to be displayed.
   */
  filters: Filter[];
  /**
   * Function to be called on filter select.
   */
  onFilterSelect?: (id?: string) => void;
  /**
   * Currently selected filter id to control the component
   */
  selectedFilterId: string;
  cardLoadingComponent?: React.JSX.Element;
};

const styles = {
  container: css`
    padding: 0;
  `,
};

/**
 * @description A generic filterable collection component that can be used to display a list of items in a grid format with filtering capabilities.
 */
const GeneralFilterableCollection = <I extends BaseItem, T>({
  title,
  Component,
  componentProps,
  collections,
  secondaryCta,
  itemsGridConfig,
  numDisplayedRows = 1,
  showMoreAmount,
  footer,
  trackingData,
  customComponentAtIndex,
  filters,
  onFilterSelect,
  selectedFilterId,
  cardLoadingComponent,
}: Props<I, T>) => {
  const trackV3 = useTracker();
  const userAgent = useUserAgent();

  // Track a pending selection purely for loading UX (keeps component controlled)
  const [pendingFilterId, setPendingFilterId] = useState<string | null>(null);

  const handleFilterSelect = (id?: string) => {
    trackV3('click_button', {
      button: { name: 'collections_filter_chip', pageConfigComponent: trackingData?.pageConfigComponent },
      carousel: { ...trackingData?.carousel, name: filters.find((f) => f.id === id)?.label },
    });
    onFilterSelect?.(id);
    setPendingFilterId(id ?? null);
  };

  // While loading, reflect most recently clicked filter as selected
  const isLoading = Boolean(pendingFilterId && pendingFilterId !== selectedFilterId);
  const currentSelectedFilterId = isLoading && pendingFilterId ? pendingFilterId : selectedFilterId;

  // Clear pending state once selection has been applied by the parent
  useEffect(() => {
    if (pendingFilterId && selectedFilterId === pendingFilterId) {
      setPendingFilterId(null);
    }
  }, [selectedFilterId, pendingFilterId]);

  const placeholderItem = { id: 'PLACEHOLDER_ITEM_ID' } as I;
  const loadingPlaceholderItem = { id: 'LOADING_ITEM_ID' } as I;

  // Resolve which collection's items to show based on the current selected filter
  const resolvedSelectedId = currentSelectedFilterId ?? collections?.[0]?.id;
  const selectedCollection = collections.find((c) => c.id === resolvedSelectedId) ?? collections?.[0];
  const selectedItems = selectedCollection?.items ?? [];

  const baseItems = customComponentAtIndex
    ? [
        ...selectedItems.slice(0, customComponentAtIndex.index),
        placeholderItem,
        ...selectedItems.slice(customComponentAtIndex.index),
      ]
    : selectedItems;

  const itemsToShow = isLoading && cardLoadingComponent ? [loadingPlaceholderItem] : baseItems;

  return (
    <FilterableShowMoreGridSection<I>
      css={styles.container}
      renderTitle={() => title}
      items={itemsToShow}
      itemsGridConfig={itemsGridConfig}
      defaultRows={numDisplayedRows}
      showMoreAmount={showMoreAmount}
      filters={filters}
      onFilterSelect={handleFilterSelect}
      selectedFilterId={currentSelectedFilterId}
      renderItem={({ item, itemIndex, itemRef }) => {
        if (isLoading && cardLoadingComponent) {
          return cardLoadingComponent;
        }
        if (customComponentAtIndex && itemIndex === customComponentAtIndex.index) {
          return customComponentAtIndex.component;
        }
        const itemProps = componentProps(item, itemIndex);
        return <Component {...itemProps} ref={itemRef} />;
      }}
      renderExpandButton={({ onClick, defaultLabel, ...rest }) => {
        return (
          <Button
            size="small"
            key="showMore"
            variant="secondary"
            onClick={() => {
              trackV3('expand_carousel', { carousel: trackingData?.carousel });
              onClick();
            }}
            {...rest}
          >
            {defaultLabel}
          </Button>
        );
      }}
      renderSecondaryCta={() => {
        return secondaryCta;
      }}
      renderFooter={() => footer}
      spacing={{ xs: 16, sm: 24 }}
      defaultMobileBreakpointMatches={userAgent && userAgent.isMobileBrowser}
      renderEmptyItems={() => <EmptyFilteredCollectionMessage filterName={currentSelectedFilterId} />}
    />
  );
};

export default GeneralFilterableCollection;
